import { get, getDatabase, ref, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { recordActivity } from './activityApi';

/**
 * Instance feature toggles at `global/features/{key}` (Inställningar → Funktioner). Booleans,
 * absent = on: only `false` hides a feature. They only hide UI; data is kept and nothing is
 * enforced. Admins write the four feature keys, `aiAssistant` is superadmin-only (rules), and the
 * `askAssistant` callable refuses only when it is `false`.
 *
 * Hyresgäster has no key: renting (tenants, leases) is on while any feature that rents to or
 * hands out to tenants is on.
 *
 * Read once per app session; `setFeatureEnabled` updates the cache and notifies subscribers so
 * the navigation and pages follow without a reload.
 */
export const FEATURES_PATH = 'global/features';

export type FeatureKey = 'parking' | 'storageUnits' | 'units' | 'keyRegister' | 'aiAssistant';
export type AppFeature = FeatureKey | 'tenants';
export type Features = Record<AppFeature, boolean>;

export const ALL_FEATURES_ON: Features = {
    parking: true, storageUnits: true, units: true, keyRegister: true, aiAssistant: true, tenants: true,
};

export function featuresFromSnapshot(raw: unknown): Features {
    const values = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
    const on = (key: FeatureKey) => values[key] !== false;
    const features = {
        parking: on('parking'),
        storageUnits: on('storageUnits'),
        units: on('units'),
        keyRegister: on('keyRegister'),
        aiAssistant: on('aiAssistant'),
    };
    return {
        ...features,
        tenants: features.parking || features.storageUnits || features.units || features.keyRegister,
    };
}

let cached: Promise<Features> | null = null;
let current: Features | null = null;
const listeners = new Set<(features: Features) => void>();

/** The toggles once read this session, else null; lets hooks start from them without a loading render. */
export function peekFeatures(): Features | null {
    return current;
}

export function getFeatures(): Promise<Features> {
    if (!cached) {
        cached = get(ref(getDatabase(), FEATURES_PATH))
            .then(snapshot => {
                current = featuresFromSnapshot(snapshot.val());
                return current;
            })
            .catch(err => {
                console.error('Failed to read feature toggles', err);
                cached = null;
                return ALL_FEATURES_ON;
            });
    }
    return cached;
}

export async function setFeatureEnabled(key: FeatureKey, enabled: boolean): Promise<void> {
    if (!getAuth().currentUser) {
        throw new Error('Du måste vara inloggad för att ändra funktioner');
    }

    try {
        await set(ref(getDatabase(), `${FEATURES_PATH}/${key}`), enabled);
    } catch (err) {
        console.error('Failed to save feature toggle', err);
        throw new Error('Kunde inte spara inställningen. Försök igen.');
    }
    recordActivity('settings.updated');
    const base = current ?? await getFeatures();
    const { tenants: _derived, ...stored } = base;
    current = featuresFromSnapshot({ ...stored, [key]: enabled });
    cached = Promise.resolve(current);
    listeners.forEach(listener => listener(current!));
}

/** Called with the new toggles after every successful `setFeatureEnabled`. */
export function onFeaturesChange(listener: (features: Features) => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
